import { memo } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import { useEffect, useRef } from 'react';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { getRiskColor, getClusterColor } from '@/utils/riskColor';
import { createVehicleIcon, createDestinationIcon } from '@/utils/markerIcons';
import { notifyTransportLine } from '@/api/ares';
import type { PointGeometry } from '@/types/map';
import { formatAresDateTimeLabel, getAresNowWallClockDate, getAresWallClockMs } from '@/utils/aresDateTime';
import { getVehicleColorMeta, getVehicleDescriptor } from '@/utils/vehicleIdentity';

function firstNonEmpty(...values: unknown[]): unknown {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    return value;
  }
  return undefined;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function VehicleMarkersComponent() {
  const map = useMap();
  const vehicleFeatures = useMapStore((s) => s.vehicleFeatures);
  const routeDataRef = useRef(useMapStore.getState().routeData);
  const routePanelOpenRef = useRef(useUIStore.getState().routePanelOpen);
  const clusterGroupRef = useRef<L.MarkerClusterGroup | null>(null);
  const markersMapRef = useRef<Map<string, L.Marker>>(new Map());

  // Keep refs in sync without triggering re-renders
  useEffect(() => {
    const unsub1 = useMapStore.subscribe((s) => {
      routeDataRef.current = s.routeData;
    });
    const unsub2 = useUIStore.subscribe((s) => {
      routePanelOpenRef.current = s.routePanelOpen;
    });
    return () => { unsub1(); unsub2(); };
  }, []);

  useEffect(() => {
    // Create cluster group
    const cluster = L.markerClusterGroup({
      chunkedLoading: true,
      iconCreateFunction: (clusterObj) => {
        const childCount = clusterObj.getChildCount();
        const children = clusterObj.getAllChildMarkers();
        let totalRisk = 0;
        children.forEach((marker: L.Marker) => {
          totalRisk += (marker.options as { riskPercent?: number }).riskPercent || 0;
        });
        const avgRisk = childCount > 0 ? totalRisk / childCount : 0;

        const color = getClusterColor(childCount, avgRisk);

        return L.divIcon({
          html: `<div style="
            background: ${color};
            width: 36px; height: 36px;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            color: white; font-weight: 700; font-size: 12px;
            font-family: 'JetBrains Mono', monospace;
            border: 3px solid rgba(255,255,255,0.3);
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          ">${childCount}</div>`,
          className: 'marker-cluster',
          iconSize: L.point(36, 36),
        });
      },
    });

    clusterGroupRef.current = cluster;
    map.addLayer(cluster);

    return () => {
      map.removeLayer(cluster);
    };
  }, [map]);

  // Update markers when features change
  useEffect(() => {
    const cluster = clusterGroupRef.current;
    if (!cluster) return;

    cluster.clearLayers();
    markersMapRef.current.clear();

    if (vehicleFeatures.length === 0) return;

    const bounds: L.LatLngBounds = L.latLngBounds([]);

    vehicleFeatures.forEach((feature) => {
      const geom = feature.geometry as PointGeometry;
      const [lon, lat] = geom.coordinates;
      const props = feature.properties;

      // --- Destination markers: different icon + simplified popup ---
      if (props.type === 'destination') {
        const destIcon = createDestinationIcon();
        const destMarker = L.marker([lat, lon], {
          icon: destIcon,
          riskPercent: 0,
        } as L.MarkerOptions & { riskPercent: number });

        const destName = props.name || props.alias || 'Destino';
        const vehicleAlias = props.vehicle_alias || '';
        const meetDate = props.meeting_date || '';

        let destPopup = `<div style="font-family:Inter,sans-serif; min-width:180px;">`;
        destPopup += `<div style="font-weight:700; font-size:13px; margin-bottom:6px; color:#ef4444;">📍 Destino</div>`;
        destPopup += `<div style="font-weight:600; font-size:13px; margin-bottom:4px;">${destName}</div>`;
        if (vehicleAlias) {
          destPopup += `<div style="opacity:0.8; font-size:12px; margin-bottom:4px;">🚛 ${vehicleAlias}</div>`;
        }
        if (meetDate && meetDate !== 'None') {
          const meetStr = formatAresDateTimeLabel(meetDate);
          destPopup += `<div style="font-size:12px;">📅 Cita: ${meetStr}</div>`;
        }
        destPopup += `</div>`;

        destMarker.bindPopup(destPopup, { maxWidth: 300, className: 'vehicle-popup' });
        bounds.extend([lat, lon]);
        cluster.addLayer(destMarker);
        return; // skip normal vehicle rendering
      }

      // --- Normal vehicle markers ---
      const color = getRiskColor(props.theft_percent, props.icon_color, props.in_reaction);
      const icon = createVehicleIcon(color);

      const marker = L.marker([lat, lon], {
        icon,
        riskPercent: props.theft_percent || 0,
      } as L.MarkerOptions & { riskPercent: number });

      // Build popup
      const alias = props.alias || props.label || 'Sin alias';
      const plate = props.plate || '';
      const economic = props.economic || '';
      const vehicleDescriptor = getVehicleDescriptor(props);
      const vehicleColor = getVehicleColorMeta(props);
      const customer = props.customer || '';
      const riskVal = parseFloat(String(props.theft_percent ?? props.risk ?? 0));
      const riskColor = riskVal >= 75 ? '#ef4444' : riskVal > 50 ? '#f59e0b' : '#10b981';
      const speed = toFiniteNumber(props.speed ?? props.current_speed);
      const speedVal = speed != null ? `${speed.toFixed(speed % 1 === 0 ? 0 : 1)} km/h` : '0 km/h';

      // ETA / Arribo / Cita logic
      const estimatedArrival = String(firstNonEmpty(
        props.eta_arrival_local,
        props.estimated_arrival,
        props.dashboard_eta_local,
        props.eta,
      ) ?? '');
      const meetingDate = props.destination_meeting_date || props.meeting_date || '';
      let isLate = false;
      let etaHtml = '';

      if (estimatedArrival) {
        const arrMs = getAresWallClockMs(estimatedArrival);
        const arrStr = formatAresDateTimeLabel(estimatedArrival);

        if (meetingDate) {
          const meetMs = getAresWallClockMs(meetingDate);
          const nowMs = getAresNowWallClockDate().getTime();
          const remainKm = toFiniteNumber(props.route_remaining_km ?? props.remaining_distance_km) ?? 0;
          isLate = (arrMs != null && meetMs != null && arrMs > meetMs) || (meetMs != null && meetMs < nowMs && remainKm > 0);
          const meetStr = formatAresDateTimeLabel(meetingDate);
          const statusBadge = isLate
            ? '<span style="color:#ef4444; font-weight:700; font-size:11px;"> RETRASADO</span>'
            : '<span style="color:#10b981; font-weight:700; font-size:11px;"> A TIEMPO</span>';
          etaHtml = `
            <div style="margin-bottom:4px; font-size:12px;">Arribo: ${arrStr}${statusBadge}</div>
            <div style="margin-bottom:4px; font-size:12px;">Cita: ${meetStr}</div>
          `;
        } else {
          etaHtml = `<div style="margin-bottom:4px; font-size:12px;">Arribo: ${arrStr}</div>`;
        }
      }

      // Risk alerts parsing
      let alertsArray: { level?: string; message?: string }[] = [];
      if (props.risk_alerts) {
        if (typeof props.risk_alerts === 'string') {
          try { alertsArray = JSON.parse(props.risk_alerts); } catch { /* ignore */ }
        } else if (Array.isArray(props.risk_alerts)) {
          alertsArray = props.risk_alerts.map((a) =>
            typeof a === 'string' ? { message: a } : a as { level?: string; message?: string }
          );
        }
      }
      const riskZoneCount = Number(props.risk_zone_count) || alertsArray.length;

      let alertBadgeHtml = '';
      if (riskZoneCount > 0) {
        let alertColor = '#3b82f6';
        if (alertsArray.some((a) => a.level === 'critical')) alertColor = '#ef4444';
        else if (alertsArray.some((a) => a.level === 'alert')) alertColor = '#f59e0b';
        else if (alertsArray.some((a) => a.level === 'warning')) alertColor = '#eab308';
        alertBadgeHtml = `
          <div style="margin-top:4px; padding:4px 8px; background:${alertColor}22; border-left:3px solid ${alertColor}; border-radius:4px; font-size:11px; color:${alertColor}; font-weight:600;">
            ⚠ ${alertsArray.length} alerta${alertsArray.length !== 1 ? 's' : ''} de riesgo
          </div>
        `;
      }

      // Remaining distance
      const distKm = firstNonEmpty(
        props.route_remaining_km,
        props.remaining_distance_km,
        props.distance_remaining_km,
        props.distance_km,
      );
      const durationMin = toFiniteNumber(
        props.route_remaining_min
        ?? props.eta_remaining_min
        ?? props.route_eta_remaining_min
        ?? props.duration_min
      );
      let distHtml = '';
      if (distKm != null) {
        const distValue = toFiniteNumber(distKm);
        distHtml = `<div style="margin-top:4px; border-top:1px solid rgba(255,255,255,0.1); padding-top:4px; font-size:11px;">`;
        if (distValue != null) {
          distHtml += `<div><strong>Restante:</strong> ${distValue.toFixed(1)} km</div>`;
        }
        if (durationMin != null) {
          distHtml += `<div><strong>Tiempo est.:</strong> ${Math.round(durationMin)} min</div>`;
        }
        distHtml += `</div>`;
      }

      // Build full popup
      let popupHtml = `<div style="font-family:Inter,sans-serif; min-width:220px;">`;
      popupHtml += `<div style="font-weight:700; font-size:14px; margin-bottom:6px; color:var(--accent-blue);">${alias}</div>`;

      if (props.in_reaction) {
        popupHtml += `<div style="color:#ef4444; font-weight:bold; margin-bottom:6px; text-transform:uppercase; font-size:11px;">⚡ En Reacción</div>`;
      }

      if (customer) {
        popupHtml += `<div style="opacity:0.9; margin-bottom:4px; font-size:12px;">🏢 ${customer}</div>`;
      }

      if (plate || economic) {
        const idBits = [];
        if (plate) idBits.push(`Placa: <strong>${plate}</strong>`);
        if (economic) idBits.push(`Eco: <strong>${economic}</strong>`);
        popupHtml += `<div style="opacity:0.9; margin-bottom:4px; font-size:11px; color:#94a3b8;">${idBits.join(' · ')}</div>`;
      }

      if (vehicleDescriptor || vehicleColor) {
        const profileBits = [];
        if (vehicleDescriptor) profileBits.push(`Unidad: <strong>${vehicleDescriptor}</strong>`);
        if (vehicleColor) {
          const colorDot = vehicleColor.swatch
            ? `<span style="display:inline-block;width:8px;height:8px;border-radius:999px;border:1px solid rgba(255,255,255,0.2);background:${vehicleColor.swatch};margin-right:4px;vertical-align:middle;"></span>`
            : '';
          profileBits.push(`Color: ${colorDot}<strong>${vehicleColor.label}</strong>`);
        }
        popupHtml += `<div style="opacity:0.92; margin-bottom:4px; font-size:11px; color:#cbd5e1;">${profileBits.join(' · ')}</div>`;
      }

      popupHtml += `<div style="margin-bottom:4px; font-size:12px;">Riesgo: <span style="color:${riskColor}; font-weight:600;">${riskVal.toFixed(1)}%</span></div>`;
      popupHtml += `<div style="margin-bottom:4px; font-size:12px;">🏎 ${speedVal}</div>`;
      popupHtml += etaHtml;
      popupHtml += alertBadgeHtml;
      popupHtml += distHtml;

      // POIs en ruta (pre-calculados por POIs Worker)
      const poiCasetas = props.poi_casetas;
      const poiGasolineras = props.poi_gasolineras;
      if (poiCasetas != null || poiGasolineras != null) {
        popupHtml += `<div style="margin-top:4px; font-size:11px; color:#94a3b8;">`;
        const parts: string[] = [];
        if (poiCasetas != null) parts.push(`${poiCasetas} casetas`);
        if (poiGasolineras != null) parts.push(`${poiGasolineras} gasolineras`);
        popupHtml += parts.join(' · ');
        popupHtml += `</div>`;
      }

      // Notificar Retraso button (only if late)
      if (isLate) {
        const tripPayload = encodeURIComponent(JSON.stringify({
          alias,
          customer,
          transport_line: props.transport_line || '',
          destination_name: props.destination_name || props.destination || '',
          meeting_date: meetingDate,
          estimated_arrival: estimatedArrival,
        }));
        popupHtml += `
          <div style="margin-top:10px;">
            <button class="popup-notify-btn" data-trip="${tripPayload}">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              NOTIFICAR RETRASO
            </button>
          </div>
        `;
      }

      // Ver Ruta y POIs button (only if has destination)
      const hasDestination = props.destination_lat && props.destination_lon;
      if (hasDestination) {
        popupHtml += `
          <div style="margin-top:8px;">
            <button class="popup-route-btn" data-alias="${alias}">🛣 Ver Ruta y POIs</button>
          </div>
        `;
      }

      popupHtml += `</div>`;

      marker.bindPopup(popupHtml, { maxWidth: 340, className: 'vehicle-popup' });
      bounds.extend([lat, lon]);
      cluster.addLayer(marker);

      // Store reference for search-to-popup
      const markerAlias = alias;
      if (markerAlias) {
        markersMapRef.current.set(markerAlias, marker);
      }
    });

    // Fit bounds (skip if route panel is open OR route data exists)
    if (bounds.isValid() && !routeDataRef.current && !routePanelOpenRef.current) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [vehicleFeatures, map]);

  // Handle search: zoom to marker and open its popup
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const alias: string | undefined = detail?.alias;
      if (!alias) return;

      const marker = markersMapRef.current.get(alias);
      if (marker && clusterGroupRef.current) {
        // Spiderfy/zoom the cluster to reveal the marker, then open popup
        clusterGroupRef.current.zoomToShowLayer(marker, () => {
          marker.openPopup();
        });
      } else if (detail?.lat != null && detail?.lon != null) {
        // Fallback: center and show a basic popup
        map.setView([detail.lat, detail.lon], 14);
      }
    };
    window.addEventListener('open-vehicle-popup', handler);
    return () => window.removeEventListener('open-vehicle-popup', handler);
  }, [map]);

  // Handle popup button clicks
  useEffect(() => {
    const handler = async (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (target.classList.contains('popup-route-btn')) {
        const alias = target.getAttribute('data-alias');
        if (alias) {
          map.closePopup();
          window.dispatchEvent(
            new CustomEvent('show-trip-route', { detail: { alias } })
          );
        }
      }

      if (target.classList.contains('popup-notify-btn')) {
        const tripStr = target.getAttribute('data-trip');
        if (tripStr) {
          try {
            const tripData = JSON.parse(decodeURIComponent(tripStr));
            target.textContent = 'Enviando...';
            target.setAttribute('disabled', 'true');
            await notifyTransportLine(tripData);
            target.textContent = '✓ Notificación enviada';
            target.style.background = '#10b981';
          } catch {
            target.textContent = 'Error al enviar';
            target.style.background = '#6b7280';
          }
        }
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [map]);

  return null;
}

export default memo(VehicleMarkersComponent);
